/**
 * Per-phase schedule helpers for the Overview "Schedule & Dates" card.
 * Mirrors apps/api/src/projects/phase-schedule.util.ts (overall %) plus the
 * display/variance math the card needs. All date math is calendar-local
 * (parseLocalDate) so it matches what the user picked, and variance is in
 * WORKING days (Mon–Fri) — how E&W reports schedule slip.
 */
import { parseLocalDate, toDateInputValue } from './dates';

export type PhaseKey = 'fabrication' | 'installation' | 'project';

export interface ProjectPhase {
  phase: PhaseKey;
  /** The baseline. Never moves. */
  originalStart: string | null;
  /**
   * The start currently being worked to (#380) — the third value that used to
   * have nowhere to live. `projects.current_start_date` is MIN of this.
   */
  plannedStart: string | null;
  originalEnd: string | null;
  /** When work actually began. Derived from Paycor on 'project' phases. */
  actualStart: string | null;
  /**
   * Who owns `actualStart`: 'paycor' = the nightly derivation refreshes it;
   * 'manual' = a PM set or cleared it and it is theirs to keep; null = never
   * spoken for (a legacy value, or an empty row the derivation may claim).
   */
  actualStartSource?: 'paycor' | 'manual' | null;
  actualEnd: string | null;
  isProjectedEnd: boolean;
  completionPct: number | null;
  budgetLaborHours: number | null;
  updatedAt?: string | null;
  updatedById?: string | null;
}

export type PhaseStatus = 'not-started' | 'in-progress' | 'complete';
export type VarianceTone = 'green' | 'performance';

/** Per-phase display metadata: label + the timeline/tick accent token. */
export const PHASE_META: Record<PhaseKey, { label: string; tick: string }> = {
  fabrication:  { label: 'Fabrication',  tick: 'var(--ew-dark)' },
  installation: { label: 'Installation', tick: 'var(--ew-quality)' },
  project:      { label: 'Project',      tick: 'var(--ew-green)' },
};

const FAB_DIVISION_ID = 7;

/**
 * Whether a project is in the Fabrication division (two-phase treatment).
 * Match on the label starting with "fab" (covers 'FAB', 'Fab', 'Fabrication' —
 * the seed says 'FAB' but live data uses 'Fabrication'), with the id as a
 * belt-and-suspenders fallback.
 */
export function isFabDivision(project: { division?: { id?: number; label?: string | null } | null } | null | undefined): boolean {
  const div = project?.division;
  if (!div) return false;
  return div.id === FAB_DIVISION_ID || String(div.label ?? '').trim().toLowerCase().startsWith('fab');
}

/** The phase keys a project should display: fab jobs get two, others one. */
export function phaseKeysFor(project: { division?: { id?: number; label?: string | null } | null } | null | undefined): PhaseKey[] {
  return isFabDivision(project) ? ['fabrication', 'installation'] : ['project'];
}

/** One phase flattened to the two dates a list cell shows. */
export interface PhaseSpan {
  phase: PhaseKey;
  /** Short label — the Projects list has one column, not a card. */
  short: string;
  start: string | null;
  end: string | null;
}

const SHORT_LABEL: Partial<Record<PhaseKey, string>> = {
  fabrication: 'Fab',
  installation: 'Install',
};

/**
 * The fab/install spans to show on the Projects list, or `null` when there are
 * none and the row's own start → completion is all there is to show.
 *
 * A fab job runs a shop phase and a field phase, and the project's single
 * range is only their outer hull — on prod every fab job's range is exactly
 * min(phase start) → max(phase end), so it hides both the gap between shop and
 * site and the overlap when they run concurrently (#334).
 *
 * `planned ?? original ?? actual` per start and `actual ?? original` per end. A
 * phase with neither date recorded is dropped rather than rendered blank —
 * several fab jobs legitimately have only one phase (the NAS barrier work is
 * booked as a fabrication job and an installation job), and an empty "Fab —"
 * line on those would read as missing data rather than as work that isn't in
 * scope.
 *
 * ⚠️ THE START LEADS WITH THE PLAN, so the list column can be labelled
 * "Planned Start" and be true on every row (#482). It used to lead with the
 * actual, which meant one column showed a started job's actual start and an
 * unstarted job's plan, with nothing saying which — and the column header could
 * not honestly name either. Every other division's row reads
 * `projects.current_start_date`, which is MIN(plannedStart) since #380, so
 * leading with the plan here is what makes the two row shapes agree.
 *
 * ⚠️ The actual start is deliberately NOT shown on the list at all. #460 asked
 * whether it should be; the answer, after the reporter was asked, is no — it
 * lives on the project's Manpower tab, beside the field hours it comes from.
 *
 * ⚠️ `actual` stays LAST in the precedence rather than dropping out of it. A
 * phase whose only recorded date is an actual start still shows it, because the
 * alternative is a row rendering "—" while holding a date. That is the one case
 * where the leading date is not a plan, and it arises only where no plan was
 * ever recorded.
 *
 * Leading with the plan also narrows the drift `earliestSpanStart` warns about:
 * a phase that HAS a planned start now sorts where the project column says it
 * does. One carrying only a baseline, or only an actual, still doesn't.
 */
export function listPhaseSpans(
  project: { division?: { id?: number; label?: string | null } | null } | null | undefined,
  phases: ProjectPhase[] | null | undefined,
): PhaseSpan[] | null {
  if (!isFabDivision(project) || !phases?.length) return null;
  const spans: PhaseSpan[] = [];
  for (const key of ['fabrication', 'installation'] as const) {
    const p = phases.find((x) => x.phase === key);
    if (!p) continue;
    const start = p.plannedStart ?? p.originalStart ?? p.actualStart;
    const end = p.actualEnd ?? p.originalEnd;
    if (!start && !end) continue;
    spans.push({ phase: key, short: SHORT_LABEL[key] ?? key, start, end });
  }
  // ⚠️ Phase lines only beat the legacy range if they can say when the job
  // STARTS and when it ENDS. Both sides can go missing, and on both sides the
  // project column then keeps a value the cell no longer shows:
  //
  //   no start — seedPhaseFromLegacy gives a seeded installation an end and no
  //     start, and ProjectWorkspace's accept-mobilization-end persists that
  //     seed. The row then renders "Install — → <end>" while the project column
  //     still holds a start the cell no longer shows. (#380 removed the OTHER
  //     half of this one: current_start_date is now written unconditionally from
  //     MIN(plannedStart), so it no longer keeps a stale value of its own — but a
  //     seeded phase with no start still leaves the cell with nothing to show.)
  //
  //   no end — the phase editor allows blank ends, and the same sync writes
  //     estimated_completion only `if (estCompl)`. The row renders "→ —" while
  //     the completion sort and the Excel export keep reading a stale
  //     estimated_completion.
  //
  // So require one of each. That also makes the invariant structural rather
  // than argued: a non-null return here guarantees the cell shows at least one
  // start and at least one end, and every key derived from it is on screen.
  if (!spans.some((s) => s.start) || !spans.some((s) => s.end)) return null;
  return spans.length > 0 ? spans : null;
}

/**
 * The earliest date any of these spans shows, or null if none carries a start.
 *
 * ⚠️ Whatever a list DISPLAYS as a job's start has to be what it sorts and
 * range-filters by, and for a fab job that is this — not the project's
 * `current_start_date`. The two can still drift, even after #380 pointed that
 * column at `MIN(plannedStart)`: a phase carrying only a baseline falls back to
 * `originalStart`, and one carrying only an actual falls back to `actualStart`.
 * Either shows a date the project column does not hold, and a row that sorted,
 * exported or filtered by that column would use a date not on screen.
 *
 * #482 removed the common case — a phase WITH a planned start now leads with it,
 * so it sorts exactly where `current_start_date` says it does.
 */
export function earliestSpanStart(spans: PhaseSpan[] | null): string | null {
  if (!spans) return null;
  const starts = spans.map((s) => s.start).filter((s): s is string => !!s);
  return starts.length ? starts.reduce((a, b) => (a <= b ? a : b)) : null;
}

/**
 * The latest date any of these spans shows, or null if none carries an end.
 *
 * ⚠️ The completion side needs this for the same reason the start does, and
 * for one more: the CSV import writes `estimated_completion` straight from the
 * file and never touches `project_phases` (applyImportUpdate), so reimporting
 * an existing fab job moves the project column while the phases — and the cell
 * — stay put. A completion sort reading the project row would then order by a
 * date nowhere on screen.
 */
export function latestSpanEnd(spans: PhaseSpan[] | null): string | null {
  if (!spans) return null;
  const ends = spans.map((s) => s.end).filter((s): s is string => !!s);
  return ends.length ? ends.reduce((a, b) => (a >= b ? a : b)) : null;
}

/**
 * Nothing has spoken for this phase's actual start, so the Paycor feed may.
 *
 * The server's claimability rule, in one place both callers read from. The
 * Overview seeds a derived date onto a phase with this, and the Manpower tab's
 * Actual Start tile shows the result — two surfaces that have to agree about
 * whether a job has begun.
 *
 * ⚠️ A MISSING PHASE COUNTS AS UNCLAIMED, which is the common case: most
 * non-fabrication jobs have no `project_phases` row at all (it is created only
 * when somebody opens the schedule drawer), and those are exactly the jobs the
 * nightly UPDATE cannot reach.
 *
 * ⚠️ A PM's explicit CLEAR is not unclaimed. It stamps `actual_start_source =
 * 'manual'` with a null date, meaning "delayed indefinitely" rather than "not
 * known yet" (#380, and the 2026-08-27 correction on it). Showing a derived date
 * over that would put #394 back, one screen along.
 */
export function actualStartUnclaimed(
  phase: Pick<ProjectPhase, 'actualStart' | 'actualStartSource'> | null | undefined,
): boolean {
  return !phase?.actualStart && !phase?.actualStartSource;
}

/**
 * Nothing has spoken for this JOB's actual start, so the Paycor feed may.
 *
 * The same rule as `actualStartUnclaimed`, asked of a whole job rather than of
 * one phase, because the Manpower tab's tile asks a job-level question and the
 * job-level Paycor date is what answers it.
 *
 * ⚠️ THE 'project' PHASE ALONE CANNOT ANSWER IT. `phaseKeysFor` gives a fab job
 * `fabrication` + `installation` and no `project` row at all, so looking for one
 * finds nothing, reads that absence as unclaimed, and shows a derived date over
 * a fab phase a PM had explicitly CLEARED — #394 one screen along, which is the
 * defect the provenance flag exists to prevent. Every phase has to be quiet.
 *
 * NO PHASES AT ALL is still unclaimed, which `every` gives for free and which is
 * the common non-fab case: the row is created only when somebody opens the
 * schedule drawer, so the jobs without one are exactly the jobs the nightly
 * UPDATE cannot reach.
 */
export function jobActualStartUnclaimed(
  phases: Pick<ProjectPhase, 'actualStart' | 'actualStartSource'>[] | null | undefined,
): boolean {
  return (phases ?? []).every((ph) => actualStartUnclaimed(ph));
}

/**
 * When work actually began on the JOB — the earliest actual start across its
 * phases, or null while it has not begun (#482).
 *
 * This is what the Manpower tab's Actual Start tile shows, beside the field
 * hours the date is derived from. It used to sit on the Schedule & Dates card;
 * asked directly, the PM who raised #460 and #482 wanted it here instead.
 *
 * ⚠️ Pass the DISPLAY phases, the ones the Overview has already seeded a derived
 * start onto. This function does not apply the Paycor fallback itself — that
 * needs `derivedActualStart` off the manpower payload, and doing it in two places
 * is how the card and the tile would come to disagree.
 *
 * Earliest rather than the 'project' phase's alone, because a fab job records
 * its actual starts per phase and the job began when the first of them did.
 */
export function jobActualStart(phases: ProjectPhase[] | null | undefined): string | null {
  const starts = (phases ?? []).map((p) => p.actualStart).filter((s): s is string => !!s);
  return starts.length ? starts.reduce((a, b) => (a <= b ? a : b)) : null;
}

/** A job's effective range when a remobilization has moved it off plan. */
export interface RemobOverride {
  start: string | null;
  end: string | null;
  /** The start comes from the latest mobilization, not the plan. */
  startSuperseded: boolean;
  /** A mobilization runs past the planned completion. */
  endSuperseded: boolean;
}

/**
 * The job-level range to show alongside a fab job's phases, or `null` when the
 * plan still holds.
 *
 * ⚠️ Judged against the plan the CELL IS SHOWING — pass the span dates, not the
 * project columns. "Superseding" is a comparison, and comparing a mobilization
 * against `estimated_completion` asks the wrong question on a fab row: the CSV
 * import rewrites that column without touching the phases, so a reimport that
 * moves it EARLIER than the phases makes an ordinary mobilization look like it
 * runs past a plan it actually finishes inside of. Phases ending 8/21, an
 * imported estimate of 7/31 and a mob ending 8/10 produced a ↺ line and a
 * completion of 8/10 while the plan visibly continued to 8/21.
 *
 * ⚠️ The two sides are judged INDEPENDENTLY, and must be. `latestMobStart` is
 * the latest-sequence mobilization's start, which the DTO lets you leave empty,
 * while `latestMobEnd` is MAX(end) over ALL mobilizations. So an end-dated
 * mobilization with no start moves the completion while `latestMobStart` stays
 * null — and keying the whole indicator off the start (as this first shipped)
 * hid a superseded completion entirely.
 */
export function remobOverride(input: {
  latestMobStart: string | null;
  latestMobEnd: string | null;
  /** The plan as displayed: for a fab row, the span dates. */
  planStart: string | null;
  planEnd: string | null;
}): RemobOverride | null {
  const startSuperseded = input.latestMobStart != null;
  // A plan with no end can't cover a mobilization's end.
  const endSuperseded = !!input.latestMobEnd
    && (!input.planEnd || input.latestMobEnd.slice(0, 10) > input.planEnd.slice(0, 10));
  if (!startSuperseded && !endSuperseded) return null;
  return {
    start: input.latestMobStart ?? input.planStart,
    end: endSuperseded ? input.latestMobEnd : input.planEnd,
    startSuperseded,
    endSuperseded,
  };
}

/**
 * Derived phase status (matches the API's convention).
 *
 * ⚠️ Reads `actualStart` and deliberately NOT `plannedStart`. This function is
 * why #380 exists: while a re-planned start had nowhere to live but
 * `actualStart`, "we intend to begin in September" was indistinguishable from
 * "work has begun", and 13 jobs on prod were reading In Progress on a date that
 * had not arrived. A planned start is a plan; only an actual start is underway.
 */
export function phaseStatus(phase: Pick<ProjectPhase, 'completionPct' | 'actualStart'>): PhaseStatus {
  if ((phase.completionPct ?? 0) >= 100) return 'complete';
  if (phase.actualStart) return 'in-progress';
  return 'not-started';
}

/** The start a phase is working to, and where it came from. */
export type EffectiveStartKind = 'actual' | 'planned' | 'original';

/**
 * The one start date a phase should be judged by, plus which of the three it is.
 *
 * ⚠️ NOT the same precedence as `listPhaseSpans`, and the divergence is the point
 * (#482). The two answer different questions:
 *
 *   • This one asks "which date should this phase be JUDGED by" — for duration,
 *     and for the start-after-end check below. There, what happened beats what
 *     was intended, so the actual start leads.
 *   • A list column asks "which date does this row SIT at". That has to be the
 *     same KIND of date on every row or the column cannot be labelled, so it
 *     leads with the plan and the actual start does not appear at all.
 *
 * They agreed until #482 only because the list had never had to name its column.
 */
export function effectiveStart(
  phase: Pick<ProjectPhase, 'originalStart' | 'plannedStart' | 'actualStart'>,
): { date: string; kind: EffectiveStartKind } | null {
  if (phase.actualStart) return { date: phase.actualStart, kind: 'actual' };
  if (phase.plannedStart) return { date: phase.plannedStart, kind: 'planned' };
  if (phase.originalStart) return { date: phase.originalStart, kind: 'original' };
  return null;
}

/**
 * The `actualStart` fragment of a phase patch — present only when the user
 * actually changed the field.
 *
 * ⚠️ THE ONE FIELD THE SCHEDULE DRAWER SENDS CONDITIONALLY, and the asymmetry is
 * the point. `actualStart` is the only phase field with a second owner: the
 * nightly Paycor derivation, which can move it while the drawer sits open. The
 * server decides provenance by comparing the incoming value against the stored
 * one, so an untouched field re-sent from a page-load snapshot would look like a
 * deliberate override — restoring a stale date and opting the phase out of the
 * feed for good, off a save the user made about something else entirely.
 *
 * Omitting it leaves the column untouched (the endpoint is a genuine partial
 * update), so presence carries real intent and the server's value-compare becomes
 * a second line of defence rather than the only one.
 *
 * Both arguments are date-input strings ('' for empty), compared verbatim: they
 * come from the same `toDateInputValue` normalisation, so an unchanged field is
 * character-identical and a changed one cannot be missed.
 *
 * Spread into the patch: `...actualStartPatch(now, opened)`.
 */
export function actualStartPatch(
  current: string,
  openedWith: string,
): { actualStart: string | null } | Record<string, never> {
  if (current === openedWith) return {};
  return { actualStart: current.trim() === '' ? null : current };
}

/**
 * A phase whose start is now LATER than the end it is working to.
 *
 * This is the shape #394 reported on job 10965: the PM moved the start to
 * 2026-09-07 because the job was delayed pending design changes, and the
 * completion stayed at 2026-07-31 — so the projects list rendered an end before
 * its own start.
 *
 * ⚠️ #380 gives that start a home; it CANNOT invent the matching end. Only a PM
 * knows when a re-planned job now finishes, so the honest thing is to make the
 * contradiction visible at the point of entry rather than derive a coherent-
 * looking date nobody stands behind. Without this the row is silently wrong and
 * nothing on the card says so — which is how 10965 stayed broken.
 *
 * Compared as YYYY-MM-DD strings, which order correctly and avoid dragging a
 * timezone into a calendar-date question. Returns null when either side is
 * missing: a job with no end recorded is incomplete, not contradictory.
 */
export function startAfterEnd(
  phase: Pick<ProjectPhase, 'originalStart' | 'plannedStart' | 'actualStart' | 'actualEnd' | 'originalEnd'>,
): { start: string; end: string; kind: EffectiveStartKind } | null {
  const start = effectiveStart(phase);
  const end = phase.actualEnd ?? phase.originalEnd;
  if (!start || !end) return null;
  const s = start.date.slice(0, 10);
  const e = end.slice(0, 10);
  return s > e ? { start: s, end: e, kind: start.kind } : null;
}

export const PHASE_STATUS_LABEL: Record<PhaseStatus, string> = {
  'not-started': 'Not Started',
  'in-progress': 'In Progress',
  complete: 'Complete',
};

/**
 * Signed count of working days (Mon–Fri) from `aISO` (exclusive) to `bISO`
 * (inclusive). Positive when b is later than a. null when either is missing.
 * e.g. Fri Jul 10 → Fri Jul 17 = +5 (Mon–Fri of the following week).
 */
export function workingDaysBetween(aISO: string | null | undefined, bISO: string | null | undefined): number | null {
  const a = parseLocalDate(aISO);
  const b = parseLocalDate(bISO);
  if (!a || !b) return null;
  const sign = b >= a ? 1 : -1;
  const start = sign > 0 ? a : b;
  const end = sign > 0 ? b : a;
  let count = 0;
  const cur = new Date(start);
  cur.setDate(cur.getDate() + 1); // exclusive of the start day
  while (cur <= end) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return sign * count;
}

/**
 * Finish/start variance vs. the baseline, in working days. ≤0 → "On time"
 * (green); >0 → "+Nd" slip (performance orange). null when dates are missing.
 */
export function varianceWorkingDays(
  originalISO: string | null | undefined,
  actualISO: string | null | undefined,
): { days: number; label: string; tone: VarianceTone } | null {
  const days = workingDaysBetween(originalISO, actualISO);
  if (days == null) return null;
  if (days <= 0) return { days, label: 'On time', tone: 'green' };
  return { days, label: `+${days}d`, tone: 'performance' };
}

/**
 * Whole WORK weeks a phase occupies, inclusive of both endpoints, rounded up.
 *
 * #426 reported the card reading a week short "in some instances". Two things
 * were wrong, and only the first was arithmetic:
 *
 *   1. The old formula was `round((end − start) / 7 days)` — an EXCLUSIVE day
 *      count fed to a round-half-up. Both halves bias downward and neither ever
 *      biases up, so the error was one-directional, exactly as reported. A job
 *      running whole Mon→Fri weeks came out right anyway (4, 11, 18, 25 days all
 *      round correctly), which is why it survived to production.
 *
 *   2. It measured CALENDAR weeks. The card is read as a schedule figure, and
 *      E&W schedules in work weeks — the same Mon–Fri basis `workingDaysBetween`
 *      already uses for the variance line directly above. A settled product
 *      decision on #426, not an inference from the code.
 *
 * Both endpoints count, and any part of a week counts as a week (`ceil`): a
 * phase occupies the day it finishes on, and a job running into a Monday has
 * taken that week. So Mon→Mon is 2 work weeks, not 1.
 *
 * ⚠️ Derived from `workingDaysBetween` rather than walking the calendar again,
 * which is what keeps the two honest. That function is EXCLUSIVE of its start
 * day and says so; this one needs both ends, so it adds the start day back when
 * the start is itself a working day. Expressing the difference as a derivation
 * makes the inclusive/exclusive relationship structural — previously the file
 * held two endpoint conventions with only one of them documented, and nothing
 * tying them together.
 *
 * A negative result means the span runs backwards — #394's shape, a re-planned
 * start later than the end it works to. The card flags that on its own
 * (`startAfterEnd`) and must not also render a negative duration, so it clamps
 * to 0 rather than reporting a length for a span that has none.
 *
 * ⚠️ Weekend work is invisible here by construction: a shop running Mon–Sat
 * reads the same as one running Mon–Fri. That follows from measuring in work
 * weeks and is accepted, not overlooked. A span falling entirely on a weekend
 * has no working days and reads 0 — as it did before this change.
 *
 * No millisecond arithmetic anywhere in this path, so daylight saving cannot
 * reach it: `workingDaysBetween` advances with `setDate`, which is DST-safe by
 * construction.
 */
export function durationWeeks(startISO: string | null | undefined, endISO: string | null | undefined): number | null {
  const signed = workingDaysBetween(startISO, endISO);
  if (signed == null) return null;
  if (signed < 0) return 0;
  const a = parseLocalDate(startISO);
  if (!a) return null;
  const dow = a.getDay();
  const startIsWorkday = dow !== 0 && dow !== 6;
  return Math.ceil((signed + (startIsWorkday ? 1 : 0)) / 5);
}

export interface OverallCompletion {
  overallPct: number;
  phases: { phase: string; pct: number; hoursShare: number }[];
}

/**
 * Labor productivity signal: how hours burned compare to progress earned.
 * `gap` > ~10 points means hours are outrunning progress (trending over budget
 * on labor — a rough inverse of Performance Factor). Returns null when we lack
 * actual hours or a budget to compare against.
 */
export interface LaborStatus {
  hoursPct: number;      // actual ÷ budget, %
  completionPct: number; // progress
  gap: number;           // hoursPct − completionPct
  overrunning: boolean;  // gap over the threshold
}
export const LABOR_OVERRUN_THRESHOLD = 10;
export function laborStatus(
  actualHours: number | null | undefined,
  budgetHours: number | null | undefined,
  completionPct: number,
): LaborStatus | null {
  if (actualHours == null || !budgetHours || budgetHours <= 0) return null;
  const hoursPct = Math.round((actualHours / budgetHours) * 100);
  const gap = hoursPct - completionPct;
  return { hoursPct, completionPct, gap, overrunning: gap > LABOR_OVERRUN_THRESHOLD };
}

/**
 * Blend phase completion % into a single project %, weighted by budget labor
 * hours (equal weighting when no phase has hours). Mirrors the server util so
 * the Edit drawer can preview the overall % live before saving.
 */
export function weightedOverall(
  phases: Pick<ProjectPhase, 'phase' | 'completionPct' | 'budgetLaborHours'>[],
): OverallCompletion {
  const rows = phases.map((p) => ({
    phase: String(p.phase),
    pct: clampPct(p.completionPct),
    hours: p.budgetLaborHours != null && p.budgetLaborHours > 0 ? p.budgetLaborHours : 0,
  }));
  const totalHours = rows.reduce((s, r) => s + r.hours, 0);

  if (totalHours <= 0) {
    const n = rows.length || 1;
    return {
      overallPct: Math.round(rows.reduce((s, r) => s + r.pct, 0) / n),
      phases: rows.map((r) => ({ phase: r.phase, pct: Math.round(r.pct), hoursShare: round3(1 / n) })),
    };
  }
  return {
    overallPct: Math.round(rows.reduce((s, r) => s + r.pct * (r.hours / totalHours), 0)),
    phases: rows.map((r) => ({ phase: r.phase, pct: Math.round(r.pct), hoursShare: round3(r.hours / totalHours) })),
  };
}

function clampPct(v: number | null | undefined): number {
  if (v == null || Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(100, v));
}
function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

// ── Mob-end vs. plan divergence ───────────────────────────────────────────────

export interface MobEndDivergence {
  /** Latest mobilization end, YYYY-MM-DD. The date accept would persist. */
  mobEnd: string;
  /** The phase that ends last — its end IS the plan's completion. */
  phase: PhaseKey;
  /** That phase's current end, YYYY-MM-DD. */
  planEnd: string;
  /**
   * True when `project_phases` already holds a row for `phase`.
   *
   * ⚠️ THIS NO LONGER SELECTS THE PAYLOAD (#383) — it selects only whether an
   * optimistic-concurrency token is meaningful. A seeded phase has no row to
   * conflict with and no `updatedAt` to send; a stored one does.
   *
   * It used to fork the payload, and the two branches failed in opposite
   * directions: a stored row wanted `phase` + `actualEnd` + `isProjectedEnd`
   * alone (sending more overwrote a concurrent editor from a stale snapshot,
   * #379 A), while a seeded one wanted the FULL displayed date set, because that
   * path INSERTs and an insert writes only the columns it is given — a sparse one
   * left a row holding `actual_end` alone, which blanked the card and lost the
   * pre-mob original end unrecoverably (job 10634).
   *
   * That fork existed because only the CLIENT chose the payload, from a snapshot,
   * and so had to predict which branch the server would take. The server now
   * seeds an INSERT's missing columns from the project's legacy dates
   * (`PHASE_LEGACY_SEED`, apps/api), so one sparse payload is correct either way
   * and a new date column can no longer be quietly destroyed by being left out.
   */
  stored: boolean;
  /** The displayed row (stored or legacy-seeded) — carries the guard token. */
  row: ProjectPhase;
}

/**
 * Is `fresh` the SAME action the user approved when they saw `shown`?
 *
 * The accept button offers one specific thing — "use Sep 2 as the Installation
 * end". If a click-time re-read finds the divergence still exists but now names
 * a different phase or a different date, that is a DIFFERENT action and nobody
 * has approved it: the button could silently update Fabrication when it said
 * Installation, or persist a mobilization end the user never saw.
 *
 * This is the original bug inverted. #379 B wrote data that was too OLD; acting
 * on an unreviewed fresh divergence writes data the user never agreed to. The
 * feature exists to be "a flag plus a human decision, not a silent overwrite",
 * so both are violations of the same rule.
 *
 * `planEnd` deliberately does NOT participate. If only the phase's current end
 * moved while the flagged phase and the date to write are unchanged, the action
 * the user approved is still exactly the action being performed. Neither does
 * `stored`, which only selects whether a concurrency token is sent.
 *
 * Declared as a type predicate, and generic, so a caller that early-returns on
 * a false result keeps `fresh` narrowed to its own full type afterwards — the
 * alternative is a redundant second null check that a reader has to reconcile
 * with this one.
 */
export function sameDivergenceAction<T extends Pick<MobEndDivergence, 'phase' | 'mobEnd'>>(
  shown: Pick<MobEndDivergence, 'phase' | 'mobEnd'>,
  fresh: T | null | undefined,
): fresh is T {
  return !!fresh && fresh.phase === shown.phase && fresh.mobEnd === shown.mobEnd;
}

/**
 * Does a mobilization run past the end the plan is working to?
 *
 * A mobilization running past the planned end already supersedes the stored date
 * for DISPLAY (see `remobOverride`) but never rewrites the plan. This surfaces
 * the divergence so the plan doesn't quietly go stale — flag plus a human
 * decision, not a silent overwrite.
 *
 * ⚠️ Shared by the render AND the accept action on purpose. They used to
 * compute it separately from the same page-load snapshot, which is how accept
 * could write a mobilization date over an end another user had already pushed
 * out further (#379 B). The action must re-read and then call this again — a
 * re-read whose result is not re-DECIDED on is the worst of both.
 *
 * `seed` is injected rather than imported so this stays free of ProjectDetail.
 */
export function mobEndDivergence(input: {
  phases: ProjectPhase[] | null | undefined;
  mobilizations: { endDate?: string | Date | null }[] | null | undefined;
  phaseKeys: PhaseKey[];
  seed: (phase: PhaseKey) => ProjectPhase;
}): MobEndDivergence | null {
  // Normalized to YYYY-MM-DD so plain string compare orders correctly.
  const latestMobEnd = (input.mobilizations ?? [])
    .map((m) => toDateInputValue(m.endDate ?? null))
    .filter(Boolean)
    .sort()
    .pop() ?? '';
  if (!latestMobEnd) return null;

  const existing = new Map((input.phases ?? []).map((ph) => [ph.phase, ph]));
  const last = input.phaseKeys
    .map((k) => {
      const row = existing.get(k) ?? input.seed(k);
      return { row, stored: existing.has(k), end: toDateInputValue(row.actualEnd ?? row.originalEnd) };
    })
    .filter((r) => r.end)
    .sort((a, b) => (a.end < b.end ? -1 : 1))
    .pop() ?? null;
  if (!last) return null;
  if (latestMobEnd <= last.end) return null;

  return {
    mobEnd: latestMobEnd,
    phase: last.row.phase as PhaseKey,
    planEnd: last.end,
    stored: last.stored,
    row: last.row,
  };
}
